import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { DocField } from '../types'
import { SignatureBox } from './FieldChip'

type Props = {
  field: DocField
  selected?: boolean
  sample?: string
  mode?: 'editor' | 'preview'
  fillMode?: 'empty' | 'sample'
  showSourceBadge?: boolean
  onSelect?: () => void
}

export function DocFieldControl({
  field,
  selected,
  sample,
  mode = 'editor',
  fillMode = 'empty',
  showSourceBadge = true,
  onSelect,
}: Props) {
  const { t } = useTranslation()
  const preview = mode === 'preview'
  const filled = preview && fillMode === 'sample'
  const value = filled
    ? (sample ?? '')
    : field.source === 'otomatik' && field.variable && !preview
      ? `{{${field.variable}}}`
      : ''

  if (field.type === 'imza') {
    return (
      <SignatureBox field={field} selected={selected} onSelect={onSelect} />
    )
  }

  return (
    <div
      role={preview ? undefined : 'button'}
      tabIndex={preview ? undefined : 0}
      onClick={(e) => {
        if (preview) return
        e.stopPropagation()
        onSelect?.()
      }}
      onKeyDown={(e) => {
        if (preview) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect?.()
        }
      }}
      className={cn(
        'w-full rounded-md border bg-card p-1.5 text-left transition-shadow',
        selected ? 'border-info ring-2 ring-info/30' : 'border-border/80',
        !preview && 'cursor-pointer hover:border-primary/40',
      )}
    >
      <div className="mb-1 flex min-w-0 items-center justify-between gap-2 px-0.5">
        <label className="min-w-0 truncate text-[11px] font-medium text-muted-foreground">
          {field.label}
          {field.required ? <span className="text-destructive"> *</span> : null}
        </label>
        {showSourceBadge ? (
          <Badge tone={field.source === 'otomatik' ? 'info' : 'neutral'} className="shrink-0 text-[9px] px-1.5 py-0">
            {t(`hr.belgeler.field.source.${field.source}`)}
          </Badge>
        ) : null}
      </div>
      <div className="pointer-events-none">
        <FieldInput field={field} value={value} muted={!preview} />
      </div>
    </div>
  )
}

function FieldInput({
  field,
  value,
  muted,
}: {
  field: DocField
  value: string
  muted: boolean
}) {
  switch (field.type) {
    case 'tarih':
      return (
        <DateField
          value={value || undefined}
          onChange={() => undefined}
          disabled={muted}
          className="h-8"
        />
      )
    case 'telefon':
      return (
        <PhoneInput
          value={value}
          onChange={() => undefined}
          disabled={muted}
          placeholder={field.placeholder}
          className="h-8"
        />
      )
    case 'sayi':
      return (
        <NumberInput
          value={value}
          onChange={() => undefined}
          disabled={muted}
          className="h-8"
        />
      )
    case 'uzun_metin':
      return (
        <Textarea
          value={value}
          readOnly
          disabled={muted}
          placeholder={field.placeholder}
          rows={2}
          className="min-h-14 resize-none text-sm"
        />
      )
    case 'secim':
      return (
        <Select
          size="sm"
          value={value}
          disabled={muted}
          onChange={() => undefined}
          placeholder={field.placeholder || '—'}
          options={(field.options ?? []).map((o) => ({ value: o, label: o }))}
        />
      )
    case 'checkbox':
      return (
        <label className="flex h-8 items-center gap-2 px-1 text-sm">
          <input type="checkbox" checked={!!value} disabled readOnly className="accent-primary" />
          <span className="text-muted-foreground">{field.helpText || field.label}</span>
        </label>
      )
    case 'eposta':
    case 'kisa_metin':
    default:
      return (
        <Input
          value={value}
          readOnly
          disabled={muted}
          placeholder={field.placeholder}
          className={cn('h-8', field.source === 'otomatik' && muted && 'font-mono text-xs text-info')}
        />
      )
  }
}
